import { useState, useEffect } from 'react';
import { EditTemplateModalProps } from './types';

export const useEditTemplateModal = ({
  open,
  currentName,
  existingTemplateNames,
  onSubmit,
  onClose,
}: EditTemplateModalProps) => {
  const [templateName, setTemplateName] = useState<string>(currentName);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setTemplateName(currentName);
      setValidationError(null);
      setIsSubmitting(false);
    }
  }, [open, currentName]);

  const validateName = (name: string): string | null => {
    const trimmedName = name.trim();

    if (!trimmedName) {
      return 'Template name is required';
    }

    if (trimmedName.length > 50) {
      return 'Template name too long (max 50 chars)';
    }

    // Check for duplicates (case-insensitive), excluding current name
    const isDuplicate = existingTemplateNames.some(
      existingName =>
        existingName.toLowerCase() === trimmedName.toLowerCase() &&
        existingName.toLowerCase() !== currentName.toLowerCase()
    );

    if (isDuplicate) {
      return 'Template name already exists';
    }

    return null;
  };

  const handleNameChange = (value: string) => {
    setTemplateName(value);
    setValidationError(null);
  };

  const handleSubmit = async () => {
    const trimmedName = templateName.trim();
    const error = validateName(trimmedName);

    if (error) {
      setValidationError(error);
      return;
    }

    // Don't submit if name hasn't changed
    if (trimmedName === currentName) {
      handleClose();
      return;
    }

    try {
      setIsSubmitting(true);
      await onSubmit(trimmedName);
      handleClose();
    } catch (err) {
      setValidationError(
        err instanceof Error ? err.message : 'Failed to update template name'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setTemplateName(currentName);
    setValidationError(null);
    setIsSubmitting(false);
    onClose();
  };

  return {
    templateName,
    validationError,
    isSubmitting,
    handleNameChange,
    handleSubmit,
    handleClose,
  };
};
